// BuildingCaptureScreen — deep-link landing for a single building.
// Three render branches per 20-ui §1.1/§1.1a: active session / no-session
// + field-staff (StartCaptureCard) / no-session + admin (Admin Preview).
// Read path uses PowerSync hooks; session-creation dispatches through
// the capture client (PowerSync-routable when the offline-write flag is
// on, REST otherwise) so Start capture resolves offline.
//
// The per-branch render tree lives in `./building-capture-content` to
// keep this file under the max-lines policy. The screen owns the
// data-loading and session-start state; the content component owns
// the per-session state (room dialog, status toggle, soft-warn).

import { useCallback, useState, type ReactElement } from 'react'
import { useParams } from 'react-router'

import type { SessionSummary } from '@citycare/shared'

import { useAuth } from '../../app/auth-provider'
import { ApiError } from '../../hooks/api'
import { useActiveSession } from '../../hooks/powersync/use-active-session'
import { useBuilding } from '../../hooks/powersync/use-building'
import { useRoomTypes } from '../../hooks/powersync/use-room-types'
import { useSessionDetail } from '../../hooks/powersync/use-session-detail'
import { Alert } from '../../ui/alert'
import { Spinner } from '../../ui/spinner'

import { useRegisteredCaptureClient } from '../client/use-registered-capture-client'
import { CaptureLayout } from '../components/capture-layout'
import { type NewRoomDialogRoomType } from '../components/new-room-dialog'

import { buildBuildingBreadcrumb } from './building-breadcrumb'
import {
  BuildingCaptureContent,
  type BuildingMetadata,
} from './building-capture-content'

function describeError(err: unknown): string {
  if (err instanceof ApiError) return err.message
  if (err instanceof Error) return err.message
  return 'Unknown error'
}

export function BuildingCaptureScreen(): ReactElement {
  const { buildingId = '' } = useParams<{ buildingId: string }>()
  // Keyed on buildingId so navigating to a different building remounts
  // the inner tree — that's how lifetime-scoped state (starting,
  // optimisticSession, etc.) resets without an effect that React's
  // `react-hooks/set-state-in-effect` rule would flag.
  return <BuildingCaptureScreenInner key={buildingId} buildingId={buildingId} />
}

function BuildingCaptureScreenInner({
  buildingId,
}: {
  buildingId: string
}): ReactElement {
  const { user, status } = useAuth()
  const buildingResult = useBuilding(buildingId)
  const activeSessionResult = useActiveSession(
    buildingId === '' ? null : { buildingId },
  )

  const [starting, setStarting] = useState(false)
  const [startError, setStartError] = useState<string | null>(null)

  // Optimistic session: the session returned by the createSession POST
  // before the `captures` PowerSync stream syncs the row down to the
  // local mirror. Without this fallback the screen would stay on
  // "Ready to start" for several seconds (or indefinitely, if the
  // sync stream is lagging) even though the server already minted a
  // session — the original bug a field user hit on dev where Start
  // capture "did nothing."
  const [optimisticSession, setOptimisticSession] =
    useState<SessionSummary | null>(null)

  // Single capture client per mount — created/registered/destroyed by the hook
  // (the factory picks PowerSync vs REST via VITE_USE_POWERSYNC_CAPTURE_CLIENT).
  // Registering it lets logout flush its coalesce before the held guard (#726).
  const captureClient = useRegisteredCaptureClient()

  // Track which optimistic id has been acknowledged by the synced
  // query — once acked, the optimistic stops acting as a fallback.
  // Without this, after the user marks the session complete
  // useActiveSession returns null (filters `completed_at IS NULL`),
  // the screen falls back to the stale optimistic, and dataPending
  // locks on a spinner forever. Building screen navigates away on
  // complete today so this is defense-in-depth; the symptom was
  // observed on the site screen (Codex P1 on PR #511). Render-time
  // conditional setState — re-renders immediately, no useEffect.
  const [ackedOptimisticId, setAckedOptimisticId] =
    useState<string | null>(null)
  if (
    optimisticSession !== null &&
    activeSessionResult.session?.id === optimisticSession.id &&
    ackedOptimisticId !== optimisticSession.id
  ) {
    setAckedOptimisticId(optimisticSession.id)
  }
  const optimisticIsLive =
    optimisticSession !== null &&
    ackedOptimisticId !== optimisticSession.id

  // Concurrent-capture guard (rootcause: M02 CSV double-count —
  // docs/plans/rootcause-echo-id-churn.DRAFT.md §7). Two surveyors with
  // concurrent sessions on one building each persist their own row per
  // (space, asset) slot and BOTH rows reach the contractual CSV export.
  // The stated operational rule is one surveyor per building; this
  // enforces it as a soft two-tap confirm. Signal: `buildings.status`
  // is GLOBALLY synced reference data (unlike other users' sessions,
  // which the per-user `captures` bucket physically never delivers —
  // see use-active-session.ts's `otherActiveCount` note; this warn
  // implements the intent that field was designed for, via the signal
  // that is actually available offline). `in_progress` with no session
  // of OUR OWN for this building ⇒ someone else's open session.
  // Best-effort by design: the mirror can lag offline, and a late
  // count must never be blocked outright ("never silently drop").
  const concurrentCaptureRisk =
    buildingResult.building?.status === 'in_progress' &&
    activeSessionResult.session === null &&
    !optimisticIsLive
  const [concurrentAcknowledged, setConcurrentAcknowledged] = useState(false)

  const handleStart = useCallback(async (): Promise<void> => {
    if (buildingResult.building === null) return
    if (user === undefined) return
    // First tap while another surveyor's session is open: arm the
    // warning and do NOT create a session. The second tap proceeds —
    // deliberate continue, not a hard block.
    if (concurrentCaptureRisk && !concurrentAcknowledged) {
      setConcurrentAcknowledged(true)
      return
    }
    setStarting(true)
    setStartError(null)
    try {
      const result = await captureClient.dispatch({
        kind: 'createSession',
        siteId: buildingResult.building.siteId,
        buildingId: buildingResult.building.id,
        userId: user.userID,
        userDisplayName: user.displayName,
      })
      if (result.kind !== 'createSession') {
        throw new Error(
          `unexpected dispatch result kind ${result.kind} for createSession`,
        )
      }
      setOptimisticSession(result.session as SessionSummary)
    } catch (err: unknown) {
      setStartError(describeError(err))
    } finally {
      setStarting(false)
    }
  }, [
    buildingResult.building,
    captureClient,
    concurrentAcknowledged,
    concurrentCaptureRisk,
    user,
  ])

  // `effectiveSession` prefers the local mirror once PowerSync has
  // caught up; until then it falls back to the optimistic POST result.
  // Once the optimistic is acknowledged it stops being a fallback so
  // post-completion `activeSessionResult.session === null` resolves to
  // a clean null (see ackedSessionIdsRef comment above).
  const effectiveSession =
    activeSessionResult.session ?? (optimisticIsLive ? optimisticSession : null)
  const sessionIdForDetail = effectiveSession?.id ?? ''
  const sessionDetailResult = useSessionDetail(sessionIdForDetail)
  const roomTypesResult = useRoomTypes()

  const errorMessage =
    buildingResult.error?.message ??
    activeSessionResult.error?.message ??
    sessionDetailResult.error?.message ??
    roomTypesResult.error?.message ??
    null
  // Codex P1 on PR #507: keep the screen on a spinner during the
  // optimistic window — i.e. when we have a freshly-minted session
  // from the POST but PowerSync hasn't mirrored it yet. If we let
  // BuildingCaptureContent render with `sessionDetail = null`, the
  // user sees an empty room list with the Complete button enabled
  // (a field user could mark an empty building complete during the
  // sync gap). The optimistic session still owns the "we've started,
  // don't show Start again" signal — the spinner just covers the
  // few hundred ms between POST-success and sync delivery so the UI
  // never renders a half-loaded screen.
  const optimisticPendingSync =
    optimisticIsLive && activeSessionResult.session === null
  const dataPending =
    buildingResult.isLoading ||
    activeSessionResult.isLoading ||
    optimisticPendingSync ||
    (activeSessionResult.session !== null &&
      (sessionDetailResult.isLoading || roomTypesResult.isLoading))
  const authPending = status === 'loading' || user === undefined

  const building: BuildingMetadata | null = buildingResult.building
    ? {
        id: buildingResult.building.id,
        siteId: buildingResult.building.siteId,
        name: buildingResult.building.name,
        floc: buildingResult.building.floc,
        status: buildingResult.building.status,
        floors: buildingResult.building.floors,
        parent: buildingResult.building.parent,
      }
    : null

  return (
    <CaptureLayout breadcrumb={buildBuildingBreadcrumb(building ?? undefined)}>
      <section className="mx-auto w-full max-w-2xl space-y-6">
        {/* Sr-only h1 for heading hierarchy + smoke-test lookup;
             hero carries the visible h2. */}
        <h1 className="sr-only">Building capture</h1>
        {errorMessage !== null ? (
          <Alert variant="error" title="Could not load this building">
            {errorMessage}
          </Alert>
        ) : dataPending || authPending || building === null ? (
          <Spinner label="Loading capture workspace…" size="md" />
        ) : (
          <>
            {startError !== null ? (
              <Alert variant="error" title="Could not start capture session">
                {startError}
              </Alert>
            ) : null}
            {concurrentAcknowledged && concurrentCaptureRisk ? (
              <Alert
                variant="warning"
                title="This building is already being captured"
              >
                Another surveyor appears to have an open capture session on
                this building. Two sessions counting the same rooms will
                double-count assets in the export. If you are sure nobody
                else is capturing here, tap the button again to continue.
              </Alert>
            ) : null}
            <BuildingCaptureContent
              key={effectiveSession?.id ?? 'no-session'}
              building={building}
              session={effectiveSession}
              sessionDetail={sessionDetailResult.detail}
              roomTypes={roomTypesResult.roomTypes.map(
                (rt): NewRoomDialogRoomType => ({
                  id: rt.id,
                  name: rt.name,
                  isSerialisable: rt.isSerialisable,
                  displayOrder: rt.displayOrder,
                }),
              )}
              onStart={handleStart}
              isStarting={starting}
            />
          </>
        )}
      </section>
    </CaptureLayout>
  )
}
